/**
 * HomePage - Generated page component.
 */
import React, { useEffect } from 'react';
import HeroCard from '../components/cards/HeroCard';
import MissionCard from '../components/cards/MissionCard';
import FeatureCard from '../components/cards/FeatureCard';
import BookingCTACard from '../components/cards/BookingCTACard';
import FolderStatStack, { FolderStat } from '../components/cards/FolderStatStack';
import { BarChart2, Bot, Database, Workflow } from 'lucide-react';

const HOME_STATS: FolderStat[] = [
  {
    value: "10+",
    label: "Workflow Integrations Supported",
    detail: "We connect your existing stack — CRMs, ERPs, cloud storage, data warehouses, and more. No custom connectors, no manual exports, no data silos."
  },
  {
    value: "4x",
    label: "Faster Reporting",
    detail: "Reporting cycles that used to take days compress to hours after automation. Dashboards update on schedule so leadership always has current numbers."
  },
  {
    value: "99.9%",
    label: "Data Processing Accuracy",
    detail: "Our pipelines include validation, error handling, and reconciliation at every stage — catching and correcting issues before they reach your reports.",
    footnote: "*Accuracy is methodology dependent"
  },
  {
    value: "24/7",
    label: "Automated Monitoring",
    detail: "Pipelines run and are monitored around the clock. Alerts fire automatically on failure so issues are caught and resolved before your team notices."
  },
  {
    value: "90%+",
    label: "Reduction in Human Processing Time",
    detail: "Clients routinely eliminate most manual data handling after go-live. Actual results depend on current workflow complexity.",
    footnote: "*Methodology dependent"
  }
];

const HomePage: React.FC = () => {
  useEffect(() => {
    document.title = 'Thornless Data Management | Beautiful Data. Painless Decisions.';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Thornless makes data management stress-free. Pipelines, analytics, AI automation, and warehousing built on the platforms you already rely on.');
  }, []);

  return (
    <main>

      <section className="layout-full" aria-label="Hero">
            <HeroCard eyebrow={"A Kuiken Technologies Service"} headline={"Making Data Beautiful Without the Thorns of Managing It."} subheadline={"Thornless takes the pain out of managing data. We automate the messy, manual work and build the analytics that turn your raw data into clear, confident business decisions."} cta={{"label": "See Our Services", "href": "/services"}} secondaryCta={{"label": "Get in Touch", "href": "/contact"}} variant={"Focused"} style={{ background: 'url(/hero-bg.svg) center/cover no-repeat, #500a1c' }}/>
      </section>
      <section className="layout-container section-padding" aria-label="Stats">
            <FolderStatStack stats={HOME_STATS} />
      </section>
      <section className="layout-container section-padding" aria-label="Mission">
            <MissionCard eyebrow={"Our Mission"} quote={"We built Thornless because data shouldn't be painful. Too many businesses are held back by manual spreadsheet work, unreliable numbers, and fragmented systems, spending more time managing data than learning from it. Our mission is to change that: replace error-prone manual processes with intelligent pipelines, trustworthy analytics, and AI-powered automation, so your team can focus on what matters."} attribution={"Adam Kuiken, Founder"}/>
      </section>
      <section className="layout-container section-padding" aria-label="ServicesPreview">
            <FeatureCard title={"Data Management Pipelines"} body={"Automated ingestion, transformation, and delivery: no manual work, no missed syncs. Reliable pipelines that scale with your business."} icon={<Workflow size={24} />}/>
            <FeatureCard title={"Analytics & Business Intelligence"} body={"Dashboards and reports that answer the questions your leadership actually cares about, not just what happened, but why, and what to do next."} icon={<BarChart2 size={24} />}/>
            <FeatureCard title={"Agentic AI Automation"} body={"AI agents grounded in your data and business rules, classifying, routing, and triggering workflows without human intervention."} icon={<Bot size={24} />}/>
            <FeatureCard title={"Data Warehousing"} body={"A centralized, cloud-native warehouse your team can query in seconds instead of hunting across disconnected systems for hours."} icon={<Database size={24} />}/>
      </section>
      <section className="layout-container section-padding" aria-label="HomeCTA">
            <BookingCTACard headline={"Ready to Make Your Data Thornless?"} description={"Book a free Discovery call. We'll dig into your data challenges and walk you through what's possible: no pressure, no sales pitch."} cta_label={"Book a Discovery Call"} cta_href={"/contact"}/>
      </section>
    </main>
  );
};

export default HomePage;
